/* Tea Ducks draft only. Appstle owns rewards; Shopify owns prices/discounts.
 * All theme cart writes share a queue. A write finishes only after the provider
 * promise, authoritative cart validation and matching section rendering finish.
 */
(() => {
  if (window.TeaDucksCartSafety) return;
  const nativeFetch = window.fetch.bind(window);
  const root = window.Shopify?.routes?.root || '/';
  const rewardLine = (line) => Boolean(line.properties?._appstle_parent_product || line.properties?._appstle_free_product);
  const planId = (line) => String(line.selling_plan_allocation?.selling_plan?.id || '');
  const fingerprint = (cart) => JSON.stringify([
    cart.item_count, cart.total_price,
    cart.items.map((line) => [line.key, line.quantity, line.original_line_price, line.final_line_price, planId(line), line.properties]),
  ]);
  const labels = JSON.parse(document.getElementById('TdCartSafetyLabels')?.textContent || '{}');
  const diagnostics = new URLSearchParams(window.location.search).get('td_cart_debug') === '1';
  const trace = (stage, data) => { if (diagnostics) console.debug('[Tea Ducks cart]', stage, JSON.stringify(data)); };
  let tail = Promise.resolve();
  let jobs = 0;
  let providerWrites = 0;
  let providerDirty = true;
  let changedAt = 0;
  let controlsUntil = 0;
  let error = null;
  let lastCart = null;
  let debugFingerprint = '';
  let refreshTimer = 0;
  let checkingCheckout = false;
  let giftRecovered = false;
  let giftRecoveryPending = false;
  const approvedSubmissions = new WeakSet();
  const pause = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

  function deadline(promise, ms = 25000) {
    let timer;
    return Promise.race([promise, new Promise((_, reject) => {
      timer = window.setTimeout(() => reject(new Error(labels.failed)), ms);
    })]).finally(() => window.clearTimeout(timer));
  }

  async function request(input, options = {}) {
    const controller = new AbortController();
    const previousSignal = options.signal || input?.signal;
    const abort = () => controller.abort();
    if (previousSignal?.aborted) abort();
    else previousSignal?.addEventListener('abort', abort, {once: true});
    const timer = window.setTimeout(abort, 25000);
    try {
      const response = await nativeFetch(input, {...options, signal: controller.signal});
      await response.clone().text();
      return response;
    } finally {
      window.clearTimeout(timer);
      previousSignal?.removeEventListener('abort', abort);
    }
  }

  async function json(url, options = {}) {
    if (diagnostics && options.body && options.method === 'POST') {
      try {
        const body = JSON.parse(options.body);
        trace('mutation', {path: new URL(String(url), window.location.origin).pathname, id: body.id, quantity: body.quantity, items: body.items?.map(item => ({id: item.id, quantity: item.quantity, selling_plan: item.selling_plan}))});
      } catch { /* Diagnostic output never changes transport semantics. */ }
    }
    const response = await request(url, {
      credentials: 'same-origin', cache: 'no-store', ...options,
      headers: {Accept: 'application/json', ...options.headers},
    });
    const data = await response.json();
    const endpoint = new URL(String(url), window.location.origin);
    trace('response', {path: endpoint.pathname, sections: endpoint.searchParams.get('sections'), status: response.status, keys: Object.keys(data), count: data.item_count, total: data.total_price});
    if (diagnostics && Array.isArray(data.items) && data.item_count !== undefined && debugFingerprint !== fingerprint(data)) {
      debugFingerprint = fingerprint(data);
      trace('cart', {count: data.item_count, total: data.total_price, lines: data.items.map(line => ({key: line.key, variant: line.variant_id, sku: line.sku, quantity: line.quantity, plan: planId(line), original: line.original_price, final: line.final_price, lineTotal: line.final_line_price, reward: rewardLine(line), rewardMarkers: {_appstle_parent_product: line.properties?._appstle_parent_product, _appstle_parent_selling_plan: line.properties?._appstle_parent_selling_plan, _appstle_free_product: line.properties?._appstle_free_product}, allocations: (line.line_level_discount_allocations || []).map(allocation => ({amount: allocation.amount, title: allocation.discount_application?.title, type: allocation.discount_application?.type, key: allocation.discount_application?.key}))}))});
    }
    if (!response.ok || data.status) throw new Error(data.description || data.message || labels.failed);
    return data;
  }

  function plans() {
    const value = window.RS?.Config?.sellingPlansJson;
    if (Array.isArray(value)) return value;
    try { return JSON.parse(value || '[]'); } catch { return []; }
  }

  // Mirror only the provider's configured initial FREE_PRODUCT rewards. Do not
  // set a new quantity cap, first-ever-customer rule or mixed-cart exclusion.
  function expectedRewards(cart) {
    const configured = plans();
    const rewards = [];
    for (const line of cart.items) {
      const id = planId(line);
      if (!id || rewardLine(line)) continue;
      const plan = configured.find((entry) => String(entry.id).split('/').pop() === id);
      if (!plan) throw new Error(labels.failed);
      for (const cycle of plan.appstleCycles || []) {
        if (Number(cycle.afterCycle) !== 0 || cycle.discountType !== 'FREE_PRODUCT') continue;
        const freeProducts = cycle.freeProducts?.length ? cycle.freeProducts : [cycle];
        for (const gift of freeProducts) {
          const variant = String(gift.freeVariantId || '');
          if (!variant) throw new Error(labels.failed);
          if (cycle.preventDuplicationFreeProduct && rewards.some((entry) => entry.variant === variant)) continue;
          rewards.push({variant, parent: String(line.id), plan: id});
        }
      }
    }
    return rewards;
  }

  function assertRewards(cart) {
    // Recovery must complete first. Markers alone never establish entitlement:
    // the configured parent/plan, quantity AND server price must also match.
    if (cart.items.some((line) => String(line.id) === String(labels.giftOnlyVariant) && !rewardLine(line))) {
      trace('gift-only-invalid', {variant: labels.giftOnlyVariant});
      throw new Error(labels.giftOnly || labels.failed);
    }
    const expected = expectedRewards(cart).map((gift) => `${gift.variant}|${gift.parent}|${gift.plan}`).sort();
    const actual = [];
    for (const line of cart.items.filter(rewardLine)) {
      if (line.final_line_price !== 0 || line.final_price !== 0) throw new Error(labels.failed);
      const key = `${line.id}|${line.properties._appstle_parent_product}|${line.properties._appstle_parent_selling_plan}`;
      for (let quantity = 0; quantity < line.quantity; quantity += 1) actual.push(key);
    }
    if (JSON.stringify(actual.sort()) !== JSON.stringify(expected)) {
      trace('reward-mismatch', {expected, actual});
      throw new Error(labels.failed);
    }
    return cart;
  }

  function paintRecovery(host, before, busy) {
    if (!labels.giftRecovered || !labels.giftRetailUrl || !host) return;
    let notice = host.querySelector('[data-td-gift-recovery]');
    if (!notice && giftRecovered) {
      notice = document.createElement('p');
      notice.dataset.tdGiftRecovery = '';
      notice.className = 'td-small';
      notice.setAttribute('role', 'status');
      notice.setAttribute('aria-live', 'polite');
      notice.append(document.createTextNode(`${labels.giftRecovered} `));
      const link = document.createElement('a');
      link.href = labels.giftRetailUrl;
      link.textContent = labels.giftRetailLabel;
      notice.append(link);
      if (before) before.before(notice);
      else host.append(notice);
    }
    if (notice) notice.hidden = busy || Boolean(error) || !giftRecovered;
  }

  function paint() {
    const busy = jobs > 0 || providerWrites > 0 || providerDirty || Date.now() < controlsUntil;
    document.documentElement.dataset.tdCartState = error ? 'error' : busy ? 'pending' : 'ready';
    for (const button of document.querySelectorAll('button[name="checkout"], [data-cart-checkout]')) {
      if (!button.hasAttribute('data-td-original-disabled')) {
        button.dataset.tdOriginalDisabled = String(button.dataset.purchasesEnabled !== undefined
          ? button.dataset.purchasesEnabled !== 'true' : button.disabled);
      }
      const merchantDisabled = button.dataset.purchasesEnabled === 'false' || button.dataset.tdOriginalDisabled === 'true';
      button.disabled = merchantDisabled || busy || Boolean(error);
      button.setAttribute('aria-busy', String(busy));
      const form = button.closest('form') || button.parentElement;
      let status = form?.querySelector('[data-td-cart-safety-status]');
      if (!status && form) {
        status = document.createElement('p');
        status.dataset.tdCartSafetyStatus = '';
        status.className = 'td-small';
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        button.before(status);
      }
      if (status) { status.hidden = !busy && !error; status.textContent = error ? (error.message === labels.giftOnly ? labels.giftOnly : labels.failed) : labels.pending; }
      paintRecovery(form, status, busy);
      let retry = form?.querySelector('[data-td-cart-retry]');
      if (error && !retry && status) {
        retry = document.createElement('button');
        retry.type = 'button';
        retry.dataset.tdCartRetry = '';
        retry.className = 'td-button td-button-secondary';
        retry.textContent = labels.retry;
        retry.addEventListener('click', () => safety.refresh().catch(() => {}));
        status.after(retry);
      }
      if (retry) { retry.hidden = !error; retry.disabled = jobs > 0 || providerWrites > 0 || Date.now() < controlsUntil; }
    }
    // A removed manual gift can leave an empty cart with no checkout form.
    for (const host of document.querySelectorAll('td-cart > section, #cart-drawer [data-cart-drawer-empty]')) paintRecovery(host, null, busy);
    for (const accelerated of document.querySelectorAll('shopify-accelerated-checkout-cart, .cart__dynamic-checkout-buttons, .td-side-cart-wallets')) {
      accelerated.inert = busy || Boolean(error);
    }
    // Ignore third-party count events while a transaction is incomplete.
    if (!busy && !error && lastCart) {
      for (const output of document.querySelectorAll('[data-cart-count]')) {
        output.textContent = `(${lastCart.item_count})`;
        output.hidden = lastCart.item_count === 0;
        output.setAttribute('aria-label', (output.dataset.cartCountLabel || output.dataset.countLabel || '__count__ items').replace('__count__', String(lastCart.item_count)));
      }
    }
  }

  function enqueue(work) {
    jobs += 1;
    paint();
    const promise = tail.catch(() => {}).then(async () => {
      try {
        const result = await work();
        error = null;
        return result;
      } catch (failure) {
        error = failure;
        throw failure;
      } finally { jobs -= 1; paint(); }
    });
    tail = promise;
    return promise;
  }

  async function quiet() {
    const until = Date.now() + 25000;
    while (providerWrites || Date.now() - changedAt < 400 || Date.now() < controlsUntil) {
      if (Date.now() > until) throw new Error(labels.failed);
      await pause(100);
    }
  }

  async function settle(recovery) {
    for (;;) {
      await quiet();
      let cart = await json(`${root}cart.js`);
      const needsProvider = cart.items.some((line) => planId(line) || rewardLine(line));
      if (needsProvider) {
        const until = Date.now() + 10000;
        while (typeof window.appstleProcessFreeProduct !== 'function' || plans().length === 0) {
          if (Date.now() > until) throw new Error(labels.failed);
          await pause(100);
        }
        // Await the provider before classifying lines; its own caught failures
        // still have to pass our independent authoritative price assertions.
        await deadline(window.appstleProcessFreeProduct(false));
        await quiet();
        cart = await json(`${root}cart.js`);
      }
      const manual = cart.items.find((line) => String(line.id) === String(labels.giftOnlyVariant) && !rewardLine(line));
      if (!manual) return assertRewards(cart);
      if (recovery.removals >= 8 || !manual.key) throw new Error(labels.giftOnly || labels.failed);
      // Never address this by variant ID: automatic discounts can split the
      // same variant into differently priced lines and change their keys.
      // Stay inside this queue job, using native transport, not another queue.
      trace('gift-only-recovery', {attempt: recovery.removals + 1, key: manual.key, quantity: manual.quantity});
      await json(`${root}cart/change.js`, {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({id: manual.key, quantity: 0})});
      recovery.removals += 1;
      giftRecoveryPending = true;
      changedAt = Date.now();
      // Loop obtains fresh keys after removal AND provider/discount changes.
    }
  }

  async function snapshot(sectionIds = []) {
    sectionIds = [...new Set([...sectionIds, document.querySelector('td-cart')?.dataset.sectionId].filter(Boolean))];
    const recovery = {removals: 0};
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const cart = await settle(recovery);
      let sections = {};
      if (sectionIds.length) {
        try { sections = await renderSections(sectionIds); }
        catch (failure) {
          trace('section-retry', {attempt: attempt + 1, message: failure.message});
          if (attempt === 2) throw failure;
          continue;
        }
      }
      await quiet();
      const after = await json(`${root}cart.js`);
      if (after.items.some((line) => String(line.id) === String(labels.giftOnlyVariant) && !rewardLine(line))) continue;
      assertRewards(after);
      if (fingerprint(cart) !== fingerprint(after)) continue;
      lastCart = after;
      providerDirty = false;
      if (giftRecoveryPending) { giftRecovered = true; giftRecoveryPending = false; }
      const result = {...after, sections};
      publish(result);
      return result;
    }
    throw new Error(labels.failed);
  }

  async function renderSections(sectionIds) {
    const groups = new Map();
    const fullCart = document.querySelector('td-cart');
    for (const id of sectionIds) {
      // The drawer is intentionally absent on the cart template. Render it in
      // the home context, while the full-cart region keeps its own template.
      const path = id === 'cart-drawer-section' ? root
        : id === fullCart?.dataset.sectionId ? fullCart.dataset.cartUrl : window.location.pathname;
      if (!groups.has(path)) groups.set(path, []);
      groups.get(path).push(id);
    }
    const sections = {};
    for (const [path, ids] of groups) {
      for (let offset = 0; offset < ids.length; offset += 5) {
        const batch = ids.slice(offset, offset + 5);
        const url = new URL(window.location.href);
        url.pathname = path;
        url.hash = '';
        url.searchParams.delete('section_id');
        if (path !== window.location.pathname) url.searchParams.delete('view');
        url.searchParams.set('sections', batch.join(','));
        // /cart negotiates cart JSON for application/json, even with sections.
        // Request HTML context; the Section Rendering API still returns JSON.
        const result = await json(url, {headers: {Accept: 'text/html'}});
        trace('sections', Object.fromEntries(batch.map(id => [id, {type: typeof result[id], length: result[id]?.length}])));
        if (batch.some(id => typeof result[id] !== 'string' || !result[id].trim())) throw new Error(labels.failed);
        Object.assign(sections, result);
      }
    }
    return sections;
  }

  function publish(cart) {
    const current = document.getElementById('shopify-section-cart-drawer-section');
    const html = cart.sections['cart-drawer-section'];
    if (current && html) {
      const wasOpen = Boolean(current.querySelector('dialog[open]'));
      const focusedId = current.contains(document.activeElement) ? document.activeElement.id : '';
      const replacement = new DOMParser().parseFromString(html, 'text/html').getElementById('shopify-section-cart-drawer-section');
      if (!replacement) throw new Error(labels.failed);
      current.replaceWith(replacement);
      if (wasOpen) document.querySelector('theme-drawer#cart-drawer')?.open?.();
      if (focusedId) document.getElementById(focusedId)?.focus({preventScroll: true});
    }
    document.dispatchEvent(new CustomEvent('tea-ducks:cart-settled', {detail: {cart}}));
  }

  function idsFrom(options, result) {
    let body = {};
    try {
      if (typeof options?.body === 'string') body = JSON.parse(options.body);
      else if (options?.body instanceof FormData || options?.body instanceof URLSearchParams) body = Object.fromEntries(options.body);
    } catch { /* The response's section keys remain authoritative. */ }
    const ids = body.sections || Object.keys(result.sections || {});
    return (Array.isArray(ids) ? ids : String(ids).split(',')).filter(Boolean);
  }

  // Native Horizon drawer changes join the same queue. Provider-owned writes
  // must bypass it to avoid deadlocking Appstle's own remove/add sequence.
  window.fetch = function(input, options) {
    const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, window.location.origin);
    const method = String(options?.method || input?.method || 'GET').toUpperCase();
    const mutation = url.origin === window.location.origin && /\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname) && method === 'POST';
    if (!mutation) return nativeFetch(input, options);
    if (url.searchParams.get('appstle') === 'true') {
      providerWrites += 1;
      providerDirty = true;
      changedAt = Date.now();
      paint();
      return request(input, options).finally(() => {
        providerWrites -= 1;
        changedAt = Date.now();
        paint();
        window.clearTimeout(refreshTimer);
        refreshTimer = window.setTimeout(() => {
          if (jobs === 0) safety.refresh().catch(() => {});
        }, 450);
      });
    }
    return enqueue(async () => {
      await quiet();
      const response = await request(input, options);
      const result = await response.clone().json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || labels.failed);
      changedAt = Date.now();
      const cart = await snapshot(idsFrom(options, result));
      const output = /\/add/.test(url.pathname) ? {...result, sections: cart.sections, item_count: cart.item_count, total_price: cart.total_price} : cart;
      const headers = new Headers(response.headers);
      headers.delete('content-length');
      headers.set('Content-Type', 'application/json');
      return new Response(JSON.stringify(output), {status: response.status, headers});
    });
  };

  const safety = window.TeaDucksCartSafety = {
    get state() { return error ? 'error' : jobs || providerWrites || providerDirty || Date.now() < controlsUntil ? 'pending' : 'ready'; },
    get cart() { return lastCart; },
    assertRewards,
    fingerprint,
    refresh(sectionIds = ['cart-drawer-section']) { return enqueue(() => snapshot(sectionIds)); },
    mutate(url, body, sectionIds = ['cart-drawer-section']) {
      return enqueue(async () => {
        await quiet();
        const before = /\/cart\/add/.test(String(url)) ? await json(`${root}cart.js`) : null;
        // One owner adds rewards: call Shopify directly, then delegate rewards
        // to the awaited provider. Do not overlap its atomic-add interceptor.
        await json(url, {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(body)});
        changedAt = Date.now();
        const cart = await snapshot(sectionIds);
        if (before && Array.isArray(body.items)) {
          const propertiesKey = (value) => JSON.stringify(Object.entries(value || {}).sort(([a], [b]) => a.localeCompare(b)));
          for (const item of body.items) {
            // Unsupported manual Welcome adds intentionally do not survive.
            // All legitimate paid adds retain the exact-quantity assertion.
            if (String(item.id) === String(labels.giftOnlyVariant) && !rewardLine(item)) continue;
            const matches = (line) => String(line.id) === String(item.id)
              && planId(line) === String(item.selling_plan || '')
              && propertiesKey(line.properties) === propertiesKey(item.properties);
            const previousQuantity = before.items.filter(matches).reduce((sum, line) => sum + line.quantity, 0);
            const actualQuantity = cart.items.filter(matches).reduce((sum, line) => sum + line.quantity, 0);
            if (actualQuantity !== previousQuantity + Number(item.quantity)) throw new Error(labels.failed);
          }
        }
        return cart;
      });
    },
  };

  function pendingControls() {
    controlsUntil = Date.now() + 650; // Covers both 300/350ms quantity debouncers.
    paint();
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(() => {
      if (jobs === 0) safety.refresh().catch(() => {});
    }, 700);
  }

  async function confirmCheckout(target) {
    if (checkingCheckout) return;
    checkingCheckout = true;
    const originalForm = target.form || target.closest?.('form');
    const formId = originalForm?.id;
    try {
      await safety.refresh();
      if (safety.state !== 'ready') return;
      if (originalForm) {
        // Section rendering may replace the original form. Submit the current
        // form and its actual checkout button, preserving Shopify semantics,
        // cart notes/properties and up-to-date quantity fields.
        const form = formId ? document.getElementById(formId) : originalForm;
        const button = form?.querySelector('[name="checkout"], [data-cart-checkout]')
          || document.querySelector(`button[form="${CSS.escape(formId || '')}"][name="checkout"]`);
        if (!form?.isConnected || !button || button.disabled) throw new Error(labels.failed);
        approvedSubmissions.add(form);
        form.requestSubmit(button);
      } else if (target.href) window.location.assign(target.href);
      else throw new Error(labels.failed);
    } catch (failure) {
      error = failure;
      paint();
    } finally { checkingCheckout = false; }
  }
  document.addEventListener('input', (event) => {
    if (event.target.closest?.('td-cart, cart-items-component') && event.target.matches('input[type="number"]')) pendingControls();
  }, true);
  document.addEventListener('quantity-selector:update', pendingControls, true);
  document.addEventListener('click', (event) => {
    if (event.target.closest?.('[data-cart-remove], button[on\\:click*="onLineItemRemove"]')) pendingControls();
    const checkout = event.target.closest?.('button[name="checkout"], [data-cart-checkout], a[href*="/checkout"]');
    if (!checkout || checkout.disabled || event.defaultPrevented) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    void confirmCheckout(checkout);
  }, true);
  document.addEventListener('submit', (event) => {
    if (approvedSubmissions.has(event.target)) {
      approvedSubmissions.delete(event.target);
      return;
    }
    if (!event.submitter?.matches?.('[name="checkout"], [data-cart-checkout]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    void confirmCheckout(event.submitter);
  }, true);
  document.addEventListener('tea-ducks:cart-updated', paint);
  new MutationObserver((records) => {
    if (records.some((record) => [...record.addedNodes].some((node) => node.nodeType === 1
      && (node.matches?.('form, button[name="checkout"]') || node.querySelector?.('button[name="checkout"]'))))) paint();
  }).observe(document.body, {childList: true, subtree: true});
  window.addEventListener('pageshow', () => safety.refresh().catch(() => {}));
  safety.refresh().catch(() => {});
})();
